/* eslint-disable react/jsx-props-no-spreading */
import { AutomatedAssignment, DirectAssignment } from '@/client/components/icons/ContinuIcons';
import {
  Box,
  Divider,
  FormControl,
  HStack,
  Input,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { useEffect, useRef } from 'react';

import AssignmentBuilderFormContainer from '../layout/AssignmentBuilderFormContainer';
import type { AssignmentFormData } from '../../types/assignmentTypes';
import HelpTextPopover from '../overlay/HelpTextPopover';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useAuthStore } from '@/client/services/state/authStore';
import { useTranslation } from 'react-i18next';

interface TypeSelectionContainerProps {
  children: React.ReactNode;
  isSelected: boolean;
  isInvalid: boolean;
  popoverContent: React.ReactNode;
  onClick: () => void;
}

function TypeSelectionContainer({
  children,
  isSelected,
  isInvalid,
  popoverContent,
  onClick,
}: TypeSelectionContainerProps) {
  return (
    <Box
      position="relative"
      paddingY={8}
      paddingX={6}
      border="1px solid"
      borderColor={isInvalid ? 'error' : isSelected ? 'warmNeutral.700' : 'warmNeutral.200'}
      borderRadius="16px"
      backgroundColor={isSelected ? 'warmNeutral.0' : 'white'}
      _hover={{
        borderColor: isInvalid ? 'error' : 'warmNeutral.700',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <Box position="absolute" top={4} right={4}>
        <HelpTextPopover popoverContent={popoverContent} />
      </Box>

      {children}
    </Box>
  );
}

export default function AssignmentDetailsForm() {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { role, is_manager: isManager } = user;

  const { watch, setValue, control, formState, clearErrors } = useFormContext<AssignmentFormData>();
  const { setActiveForm } = useAdminAssignmentsStore();
  const workflowNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => workflowNameInputRef.current?.focus(), []);

  useEffect(() => {
    setActiveForm('generalDetails');
  }, [setActiveForm]);

  const { simpleType } = watch();

  const { errors } = formState;
  const hasError = !!errors?.settings?.name;
  const hasTypeSelectionError = !!errors?.simpleType;

  return (
    <AssignmentBuilderFormContainer
      headerComponent={
        <Stack spacing={0}>
          <Text fontSize="24px" fontWeight={600}>
            {t('admin.assignments.create.assignment')}
          </Text>

          {hasError && (
            <Text variant="error">{t('admin.assignments.automations.requiredFieldsError')}</Text>
          )}
        </Stack>
      }
      formComponent={
        <Stack spacing={8}>
          <Stack>
            <Text variant="createLabel" color={hasError ? 'error' : 'baseBlack'}>
              {t('admin.assignments.details.name')}
            </Text>

            <Controller
              name="settings.name"
              control={control}
              rules={{ required: `${t('admin.assignments.details.name.error')}` }}
              render={({ field }) => (
                <FormControl isInvalid={hasError}>
                  <Input
                    {...field}
                    variant="create"
                    autoFocus
                    placeholder={`${t('admin.assignments.details.name.placeholder')}`}
                    isRequired
                  />
                </FormControl>
              )}
            />
          </Stack>

          <Divider />

          <Stack spacing={4}>
            <Text variant="createHeading">
              {t('admin.assignments.details.selectAssignmentType')}
            </Text>

            {role !== 'creator' && (!isManager || role === 'admin') && (
              <TypeSelectionContainer
                isSelected={simpleType === 'automated'}
                isInvalid={hasTypeSelectionError}
                popoverContent={
                  <>
                    <Text fontSize="12px" fontWeight={400}>
                      {t('admin.assignments.details.examples')}
                    </Text>

                    <UnorderedList fontSize="12px" fontWeight={400}>
                      <ListItem>{t('admin.assignments.details.automated.example1')}</ListItem>
                      <ListItem>{t('admin.assignments.details.automated.example2')}</ListItem>
                      <ListItem>{t('admin.assignments.details.automated.example3')}</ListItem>
                    </UnorderedList>
                  </>
                }
                onClick={() => {
                  setValue('simpleType', 'automated');
                  clearErrors('simpleType');
                }}
              >
                <HStack>
                  <AutomatedAssignment alignSelf="start" boxSize={14} />

                  <Stack>
                    <Text fontSize="16px" fontWeight={700}>
                      {t('admin.assignments.details.automated')}
                    </Text>

                    <Text fontSize="14px" fontWeight={400}>
                      {t('admin.assignments.details.automated.helpText')}
                    </Text>
                  </Stack>
                </HStack>
              </TypeSelectionContainer>
            )}

            <TypeSelectionContainer
              isSelected={simpleType === 'pointInTime'}
              isInvalid={hasTypeSelectionError}
              popoverContent={
                <>
                  <Text fontSize="12px" fontWeight={400}>
                    {t('admin.assignments.details.examples')}
                  </Text>

                  <UnorderedList fontSize="12px" fontWeight={400}>
                    <ListItem>{t('admin.assignments.details.direct.example1')}</ListItem>
                    <ListItem>{t('admin.assignments.details.direct.example2')}</ListItem>
                    <ListItem>{t('admin.assignments.details.direct.example3')}</ListItem>
                  </UnorderedList>
                </>
              }
              onClick={() => {
                setValue('simpleType', 'pointInTime');
                setValue('settings.type', WorkflowType.PointInTime);
                setValue('formBoundType', WorkflowType.PointInTime);
                clearErrors('simpleType');
              }}
            >
              <HStack>
                <DirectAssignment alignSelf="start" boxSize={14} />

                <Stack>
                  <Text fontSize="16px" fontWeight={700}>
                    {t('admin.assignments.details.direct')}
                  </Text>

                  <Text fontSize="14px" fontWeight={400}>
                    {t('admin.assignments.details.direct.helpText')}
                  </Text>
                </Stack>
              </HStack>
            </TypeSelectionContainer>
          </Stack>
        </Stack>
      }
    />
  );
}
